export type ProductName = 'reward' | 'limited' | 'premium' | 'complete'
export type SizeTier = 'small' | 'medium' | 'large' | 'enterprise'

export interface ProductFeature {
  label: string
  included: boolean
}

export interface ProductConfig {
  name: ProductName
  displayName: string
  description: string
  features: ProductFeature[]
  adminLimit: string
  trialDays: number
  prices: Record<SizeTier, number | null> // null = Contact Us
}

// All features in consistent order across all products
export const PRODUCTS: ProductConfig[] = [
  {
    name: 'reward',
    displayName: 'Purcent Rewards',
    description: 'Comprehensive Reward Statements',
    trialDays: 7,
    adminLimit: '2',
    features: [
      { label: 'Ledger Accounts', included: false },
      { label: 'Accruals', included: false },
      { label: 'Templates', included: false },
      { label: 'Approvals', included: false },
      { label: 'Advanced Approvals', included: false },
      { label: 'Advanced Formulas', included: false },
      { label: 'Smart Imports*', included: true },
      { label: 'Integrations', included: false },
      { label: 'SSO', included: false },
      { label: 'Composite Targets', included: false },
      { label: 'International Currency', included: false },
      { label: 'Total Reward Statements', included: true },
    ],
    prices: { small: 300, medium: 600, large: 1200, enterprise: null },
  },
  {
    name: 'limited',
    displayName: 'Purcent Limited',
    description: 'Core compensation planning',
    trialDays: 30,
    adminLimit: '2',
    features: [
      { label: 'Ledger Accounts', included: true },
      { label: 'Accruals', included: true },
      { label: 'Templates', included: true },
      { label: 'Approvals', included: true },
      { label: 'Advanced Approvals', included: false },
      { label: 'Advanced Formulas', included: false },
      { label: 'Smart Imports*', included: false },
      { label: 'Integrations', included: false },
      { label: 'SSO', included: false },
      { label: 'Composite Targets', included: false },
      { label: 'International Currency', included: false },
      { label: 'Total Reward Statements', included: false },
    ],
    prices: { small: 1200, medium: 2400, large: 4800, enterprise: null },
  },
  {
    name: 'premium',
    displayName: 'Purcent Premium',
    description: 'Advanced compensation platform',
    trialDays: 30,
    adminLimit: 'Unlimited',
    features: [
      { label: 'Ledger Accounts', included: true },
      { label: 'Accruals', included: true },
      { label: 'Templates', included: true },
      { label: 'Approvals', included: true },
      { label: 'Advanced Approvals', included: false },
      { label: 'Advanced Formulas', included: true },
      { label: 'Smart Imports*', included: false },
      { label: 'Integrations', included: false },
      { label: 'SSO', included: false },
      { label: 'Composite Targets', included: true },
      { label: 'International Currency', included: false },
      { label: 'Total Reward Statements', included: false },
    ],
    prices: { small: 3200, medium: 4400, large: 5800, enterprise: null },
  },
  {
    name: 'complete',
    displayName: 'Purcent Complete',
    description: 'Full platform with all features',
    trialDays: 30,
    adminLimit: 'Unlimited',
    features: [
      { label: 'Ledger Accounts', included: true },
      { label: 'Accruals', included: true },
      { label: 'Templates', included: true },
      { label: 'Approvals', included: true },
      { label: 'Advanced Approvals', included: true },
      { label: 'Advanced Formulas', included: true },
      { label: 'Smart Imports*', included: true },
      { label: 'Integrations', included: true },
      { label: 'SSO', included: true },
      { label: 'Composite Targets', included: true },
      { label: 'International Currency', included: true },
      { label: 'Total Reward Statements', included: true },
    ],
    prices: { small: null, medium: null, large: null, enterprise: null },
  },
]

export const SIZE_TIERS = [
  { key: 'small' as const, max: 25, label: 'Up to 25 Employees' },
  { key: 'medium' as const, max: 75, label: 'Up to 75 Employees' },
  { key: 'large' as const, max: 225, label: 'Up to 225 Employees' },
  { key: 'enterprise' as const, max: Infinity, label: 'More than 225 Employees' },
]

export function getSizeTierForCount(count: number): SizeTier {
  if (count <= 25) return 'small'
  if (count <= 75) return 'medium'
  if (count <= 225) return 'large'
  return 'enterprise'
}

export function getSizeTierLabel(tier: SizeTier): string {
  const found = SIZE_TIERS.find((t) => t.key === tier)
  return found?.label ?? ''
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

/**
 * Maps a subscription tier label (e.g., "reward_small") to a ProductName + SizeTier.
 * Also supports matching by tier name and maxUsers as a fallback.
 */
export function mapTierToProduct(
  tierLabel: string,
  tierName?: string,
  maxUsers?: number
): { product: ProductName; sizeTier: SizeTier } | null {
  // Try label-based matching first (e.g., "reward_small", "limited_medium")
  const labelLower = tierLabel.toLowerCase()
  const productNames: ProductName[] = ['reward', 'limited', 'premium', 'complete']
  const sizeTiers: SizeTier[] = ['small', 'medium', 'large', 'enterprise']

  for (const product of productNames) {
    for (const size of sizeTiers) {
      if (labelLower.includes(product) && labelLower.includes(size)) {
        return { product, sizeTier: size }
      }
    }
  }

  // Fallback: match by name and maxUsers
  if (tierName && maxUsers !== undefined) {
    const nameLower = tierName.toLowerCase()
    const matchedProduct = productNames.find((p) => nameLower.includes(p))
    if (matchedProduct) {
      const sizeTier = getSizeTierForCount(maxUsers)
      return { product: matchedProduct, sizeTier }
    }
  }

  return null
}

/**
 * Returns the price for a product at a given size tier, or null if Contact Us.
 */
export function getProductPrice(product: ProductName, sizeTier: SizeTier): number | null {
  const config = PRODUCTS.find((p) => p.name === product)
  return config?.prices[sizeTier] ?? null
}
